<template>
  <!-- FLOW-V5 §3.2 — `inline`: та же форма входа без окна, внутри чужой
       карточки (чек-лист переноса). Второе окно поверх попапа не нужно. -->
  <component
    :is="inline ? 'div' : BaseModal"
    v-bind="inline ? { class: 'registration-inline' } : { open: modelValue, title: $t('auth.login'), zIndex: 1000 }"
    @close="close"
  >
    <div class="modal-content">
      <!-- Hidden Captcha Widget — initialized but not visible, triggered on
           submit. Живёт вне шагов: тот же виджет нужен и на шаге кода, откуда
           работает «отправить ещё раз». -->
      <div class="captcha-hidden-container">
        <CaptchaWidget
          ref="captchaWidgetRef"
          @verified="onCaptchaVerified"
          @expired="onCaptchaExpired"
          @error="onCaptchaError"
        />
      </div>

      <!-- Step 1: Email only (IGRA-20 — registration reduced to a single
           field per operator instruction). Referral, if any, was already
           captured silently by /r or /c into the `referal` cookie
           (persistReferralCode) — the backend resolves it from there, no
           visible inviter field needed. Password is generated internally
           (not user-facing) since the ongoing auth channel is the OTP code
           sent on this step's submit, not a remembered password. -->
      <!-- Спринт 14: вопрос «куда прислать код» идёт ПЕРВОЙ веткой, и это не
           стилистика. Фаза выбора оставляет шаг равным 'email' (кода ещё нет,
           переходить на его ввод нельзя), поэтому ветка ниже 'email' никогда
           бы не отрисовалась — человек увидел бы ту же форму адреса и решил,
           что кнопка не работает. -->
      <template v-if="choiceChannels.length">
        <OtpChannelChoice
          :channels="choiceChannels"
          :busy="isLoading"
          @pick="handleChoicePick"
        />
        <div v-if="generalError" class="alert alert-error">{{ generalError }}</div>
        <div class="resend-link">
          <a href="#" @click.prevent="goBackToEmail">{{ $t('auth.changeEmail') }}</a>
        </div>
      </template>

      <template v-else-if="step === 'email'">
        <!-- Внутри чек-листа заголовок уже есть — у пункта «Войдите». -->
        <h2 v-if="!inline" class="modal-title">{{ $t('auth.login') }}</h2>
        <p class="modal-subtitle">
          {{ $t('auth.registrationSubtitle') }}
        </p>

        <form @submit.prevent="handleEmailSubmit" class="auth-form">
          <!-- General error alert -->
          <div v-if="generalError" class="alert alert-error">
            <i class="zmdi zmdi-alert-circle" />
            <span>{{ generalError }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('auth.email') }}</label>
            <div class="email-input-container">
              <input
                v-model="form.email"
                type="email"
                class="form-input ym-disable-keys"
                :class="{ 'form-input--error': fieldErrors.email }"
                :placeholder="$t('auth.enterEmail')"
                required
                @focus="isEmailFocused = true"
                @blur="handleEmailBlur"
              />
              <EmailDomainSuggestions
                :email="form.email"
                :is-focused="isEmailFocused"
                @select="handleEmailSuggestionSelect"
              />
            </div>
            <span v-if="fieldErrors.email" class="form-error">{{ fieldErrors.email }}</span>
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading"
          >
            {{ isLoading ? $t('auth.loading') : submitLabelKey ? $t(submitLabelKey) : $t('auth.landing.ctaPrimary') }}
          </button>
        </form>

        <!-- mode="login", хотя модалка заодно и регистрирует: режим register
             в SocialAuthButtons сначала требует логин пригласителя, а у гостя
             с лендинга его нет — клик по Google упирался в окно «Укажите
             пригласителя» вместо перехода в Google. Соседний вход по e-mail
             пригласителя тоже не спрашивает: его молча берут из куки referal,
             которую поставили /r или /c. На бэкенде разницы нет — экшен
             google-signin вызывает google-signup и так же заводит новичка. -->
        <SocialAuthButtons
          mode="login"
          :stay-on-site="inline"
          :tradein-resume="tradeinResume ?? null"
          @totp-required="enterTotpStep({ token: $event, approval_type: 'totp' })"
        />

        <div class="legal-text" v-html="$t('auth.legalDisclaimer', { privacy: '<a href=\'/law/privacy\' target=\'_blank\'>' + $t('auth.privacyPolicy') + '</a>', terms: '<a href=\'/law/offer\' target=\'_blank\'>' + $t('auth.termsOfUse') + '</a>' })" />
      </template>

      <!-- Step 2: OTP — the only confirmation step (IGRA-20). The account
           was already created (silently, with a generated password) by the
           email step above — this /profile/login-by-code + verify
           round-trip is what actually grants access to the dashboard. -->
      <!-- IGRA-387: почтовый сервер письмо отклонил — вместо формы кода
           предлагаем другой канал, «отправить ещё раз» не показываем. -->
      <template v-else-if="step === 'otp' && (otpStatus === 'rejected' || otpStatus === 'confirmed')">
        <h2 class="modal-title">{{ $t('auth.checkInbox') }}</h2>

        <OtpDeliveryFallback
          :status="otpStatus"
          :channels="otpChannels"
          :email="form.email"
          @change-email="goBackToEmail"
          @await-confirmation="awaitOtpConfirmation(form.email)"
        />
      </template>

      <template v-else-if="step === 'otp'">
        <h2 class="modal-title">{{ $t('auth.checkInbox') }}</h2>
        <!-- Код мог уйти в Telegram: адрес почты на этом экране был неправдой. -->
        <p class="modal-subtitle">
          <template v-if="codeReceiver === 'telegram'">{{ $t('auth.otpChoice.sentToTelegram') }}</template>
          <template v-else>{{ $t('auth.codeSent') }} <strong class="ym-hide-content">{{ receiverAddress || form.email }}</strong>.</template>
        </p>

        <div v-if="canSwitchChannel" class="resend-link">
          <a href="#" data-test="switch-channel" @click.prevent="switchChannel">
            {{ codeReceiver === 'telegram' ? $t('auth.otpChoice.switchToEmail') : $t('auth.otpChoice.switchToTelegram') }}
          </a>
        </div>

        <form @submit.prevent="handleOtpSubmit" class="auth-form">
          <div class="form-group">
            <label class="form-label">{{ $t('auth.enterCode') }}</label>
            <div class="code-digits">
              <input
                v-for="(_, index) in 6"
                :key="index"
                ref="otpInputs"
                type="text"
                inputmode="numeric"
                maxlength="1"
                v-model="otpDigits[index]"
                @input="handleOtpInput($event, index)"
                @keydown.delete="handleOtpDelete(index)"
                @paste="handleOtpPaste"
                class="code-digit ym-disable-keys"
                autocomplete="one-time-code"
                :disabled="codeBurned"
              />
            </div>
          </div>

          <div v-if="generalError" class="alert alert-error">
            <i class="zmdi zmdi-alert-circle" />
            <span>{{ generalError }}</span>
          </div>

          <!-- IGRA-387: письмо доставлено или задерживается — поле кода на месте. -->
          <OtpDeliveryFallback
            :status="otpStatus"
            :channels="otpChannels"
            :email="form.email"
            @change-email="goBackToEmail"
            @await-confirmation="awaitOtpConfirmation(form.email)"
          />

          <button type="submit" class="btn btn-primary btn-block" :disabled="isLoading || !isOtpComplete || codeBurned">
            {{ isLoading ? $t('auth.verifying') : $t('auth.continue') }}
          </button>

          <div class="resend-link">
            <a
              href="#"
              data-test="otp-resend"
              :class="{ disabled: isLoading || isResendPaused }"
              @click.prevent="handleResendOtp"
            >
              {{ isLoading ? $t('auth.sending') : codeBurned ? $t('auth.codeLimit.requestNew') : $t('auth.resendCode') }}
            </a>
          </div>

          <div class="resend-link">
            <a href="#" @click.prevent="goBackToEmail">{{ $t('auth.changeEmail') }}</a>
          </div>
        </form>
      </template>

      <!-- OTP-R6c: код из письма верный, но у аккаунта включено приложение-
           аутентификатор — вход ещё не выдан, нужен его код. -->
      <template v-else-if="step === 'totp'">
        <LoginTotpStep
          :pending-token="pendingToken"
          @approved="handleTotpApproved"
          @expired="handleTotpExpired"
        />
        <div class="resend-link">
          <a href="#" data-test="totp-back" @click.prevent="goBackToEmail">{{ $t('auth.backToLogin') }}</a>
        </div>
      </template>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useRouter } from 'vue-router'
import { parseApiError } from '@/shared/utils/apiErrorParser'
import { ProfileApi } from '@/modules/profile/api/profileApi'
import BaseModal from '@/shared/components/base/BaseModal.vue'
import CaptchaWidget from '@/shared/components/CaptchaWidget.vue'
import SocialAuthButtons from './SocialAuthButtons.vue'
import EmailDomainSuggestions from '@/shared/components/EmailDomainSuggestions.vue'
import { getCaptchaFieldName } from '@/shared/utils/captchaConfig'
import { isCaptchaSessionExpiredError, requestCaptchaToken } from '@/shared/utils/captchaRetryService'
import { readStoredInvite } from '@/modules/vpn/utils/poolInvite'
import { signInDone } from '../utils/signInDone'
import OtpDeliveryFallback from './OtpDeliveryFallback.vue'
import OtpChannelChoice from '@/modules/auth/components/OtpChannelChoice.vue'
import LoginTotpStep from '@/modules/auth/components/LoginTotpStep.vue'
import { useOtpDeliveryStatus } from '@/modules/auth/composables/useOtpDeliveryStatus'
import { codeLimitOf, useCodeRequestPause } from '@/shared/utils/codeLimit'

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
  /**
   * Адрес, с которого человек пришёл: приглашение в общую подписку,
   * протухшая magic-ссылка. Подставляется в поле при открытии.
   */
  initialEmail?: string
  /** Форма без окна — внутри карточки страницы (FLOW-V5 §3.2). */
  inline?: boolean
  /** Адрес сессии переноса: вход через Telegram идёт `tgtradein_…`. */
  tradeinResume?: string | null
  /**
   * Ключ текста главной кнопки. Без него — «Получить VPN»; страница `/5g`
   * передаёт свой: слова VPN в 5G-потоке нет (решение 2 спеки sprint-17).
   */
  submitLabelKey?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

/**
 * IGRA-387 — судьба письма с первым кодом. Первый вход после регистрации
 * упирается в ту же почту, что и обычный вход, поэтому экран одинаковый.
 */
const {
  status: otpStatus,
  loginToken: otpLoginToken,
  channels: otpChannels,
  start: startOtpStatus,
  awaitConfirmation: awaitOtpConfirmation,
  stop: stopOtpStatus,
  reset: resetOtpStatus,
} = useOtpDeliveryStatus()

const profileStore = useProfileStore()
const router = useRouter()

// Two-step registration flow (IGRA-20, simplified per operator instruction):
// email only -> OTP. No password field, no manual inviter field — referral
// (if any) was already captured by /r or /c into the `referal` cookie
// before this modal ever opens (persistReferralCode, router/index.ts), and
// the backend resolves it from that cookie automatically
// (Registration.php: `$DATA['referral'] ?: $cookies->referal ?: site default`).
// Google/Telegram social signup (SocialAuthButtons) bypasses OTP entirely —
// the provider already verified the email.
type Step = 'email' | 'otp' | 'totp'
const step = ref<Step>('email')
// OTP-R6c: pending-ключ до кода из приложения. Живёт только в памяти окна:
// в хранилище входа он попадает, лишь когда сервер вход подтвердил.
const pendingToken = ref('')
// F1: 32 — код сожжён пятой ошибкой, вводить его дальше бесполезно.
const codeBurned = ref(false)
// F1: 33 — новый код сервер не выдаст ещё 15 минут.
const { paused: isResendPaused, pause: pauseResend } = useCodeRequestPause()
const captchaWidgetRef = ref<InstanceType<typeof CaptchaWidget> | null>(null)

const form = reactive({
  email: '',
})

// Подставляем адрес, с которого пришли (приглашение, протухшая magic-ссылка).
// Именно подставляем, а не запираем: человек волен войти любой своей почтой —
// приглашение адресом больше не ограничено. Закрытие модалки чистит поле
// (`resetState`), поэтому подстановка висит на открытии, а не на mount.
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen && props.initialEmail) form.email = props.initialEmail
  },
  { immediate: true },
)

const isLoading = ref(false)

/**
 * Спринт 14: бэкенд вернул фазу выбора — код не отправлен, ждём ответа
 * «куда прислать». Пустой список означает «вопроса нет».
 */
const choiceChannels = ref<string[]>([])
// Каналы прошлого выбора: по ним решаем, показывать ли «отправить иначе».
const lastChoiceChannels = ref<string[]>([])
// Канал, который выбрал человек. Уходит в тело повторного запроса.
const chosenChannel = ref<'telegram' | 'email' | null>(null)
// Свежая одноразовая сессия капчи из фазы выбора: с ней уходит второй запрос,
// поэтому капчу человек проходит один раз, а не дважды.
const choiceCaptchaSession = ref<string | null>(null)
// Куда код ушёл на самом деле — по ответу бэкенда, а не по нашей догадке.
const codeReceiver = ref<'email' | 'telegram'>('email')
const receiverAddress = ref('')
let requestVersion = 0
onBeforeUnmount(() => { requestVersion++ })

const canSwitchChannel = computed(() => lastChoiceChannels.value.length > 1)

const fieldErrors = ref<Record<string, string>>({})
const generalError = ref<string | null>(null)
const isEmailFocused = ref(false)

/**
 * Прошлый выбор относится к прошлому адресу: и при закрытии модалки, и при
 * правке адреса он должен пропасть целиком — иначе следующий человек за тем
 * же браузером получит чужой канал.
 */
const resetChoice = () => {
  choiceChannels.value = []
  lastChoiceChannels.value = []
  chosenChannel.value = null
  choiceCaptchaSession.value = null
  codeReceiver.value = 'email'
  receiverAddress.value = ''
}

const handleEmailSuggestionSelect = (email: string) => {
  form.email = email
  // Note: suggestions will hide automatically because selected email has a complete domain
}

const handleEmailBlur = () => {
  // Small delay to allow click on suggestion to process before hiding
  setTimeout(() => {
    isEmailFocused.value = false
  }, 150)
}

const clearErrors = () => {
  fieldErrors.value = {}
  generalError.value = null
}

const close = () => {
  emit('update:modelValue', false)
  resetState()
}

const resetState = () => {
  requestVersion++
  isLoading.value = false
  step.value = 'email'
  pendingToken.value = ''
  form.email = ''
  resetChoice()
  codeBurned.value = false
  otpDigits.fill('')
  resetOtpStatus()
  clearErrors()
}

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) resetState()
})

// Back from the OTP step: the account already exists, but the user may have
// mistyped the address — let them correct it and re-run the email step.
const goBackToEmail = () => {
  requestVersion++
  isLoading.value = false
  step.value = 'email'
  pendingToken.value = ''
  resetChoice()
  codeBurned.value = false
  otpDigits.fill('')
  resetOtpStatus()
  clearErrors()
}

// Инвалидируем старую попытку сразу, сохраняя редактируемый адрес.
watch(() => form.email, goBackToEmail, { flush: 'sync' })

// Step 1: Validate email, then trigger captcha popup (dev: auto-bypassed)
const handleEmailSubmit = async () => {
  if (isLoading.value) return
  clearErrors()

  if (!form.email || !form.email.includes('@')) {
    fieldErrors.value.email = t('auth.enterEmail')
    return
  }

  captchaWidgetRef.value?.showCaptcha?.()
}

const onCaptchaExpired = () => {
  // no-op: token is only used once, immediately, by performSignup
}

const onCaptchaError = () => {
  generalError.value = t('auth.captchaError')
}

// Called once captcha verifies (or is bypassed in dev) — creates the account
// and moves straight to the mandatory OTP step.
const onCaptchaVerified = async (token: string) => {
  if (isLoading.value || !props.modelValue) return
  const version = ++requestVersion
  // OTP-R3: токен и прочие эффекты ответа — только пока попытка актуальна.
  const isCurrent = () => version === requestVersion
  isLoading.value = true
  clearErrors()

  try {
    const payload: Record<string, unknown> = {
      email: form.email,
      [getCaptchaFieldName(token)]: token,
    }

    // Спринт 14: канал уходит ТОЛЬКО когда человек его выбрал. Без него
    // бэкенд сам решает, задать ли вопрос: одному каналу вопрос не нужен.
    if (chosenChannel.value) {
      payload.channel = chosenChannel.value
    }

    // Второй запрос после выбора идёт с сессией капчи, которую выдала фаза
    // выбора: показывать капчу второй раз незачем. Токен одноразовый, поэтому
    // сразу его и забываем. Имя параметра на проводе — с ДЕФИСОМ, в ответе
    // оно приходит с подчёркиванием.
    if (choiceCaptchaSession.value) {
      delete payload[getCaptchaFieldName(token)]
      payload['captcha-session'] = choiceCaptchaSession.value
      choiceCaptchaSession.value = null
    }

    // /c/:login circle invite (API-CONTRACT.md §Реф-ссылки): flag travels in
    // the signup payload, cleared below once the account is created.
    if (localStorage.getItem('circle_invite') === '1') {
      payload.circle_invite = 1
    }

    // IGRA-110 R7 — приглашение в общую подписку. Спонсором пришедшего по
    // ссылке `/invite/<token>` становится ВЛАДЕЛЕЦ пула, а `wpu_users.referal`
    // пишется один раз, при регистрации: не отдать токен здесь значит навсегда
    // оставить спонсором владельца сайта — приглашённый не сядет в круг
    // владельца при своей первой оплате, и тот потеряет обещанное место и
    // скидку (US-5). Флаг круга и токен пула живут рядом и друг друга не
    // отменяют.
    //
    // Запись НЕ стираем: по этому же токену «Главная» сразу после входа зовёт
    // `POST /vpn/pool/accept` — само вступление в пул делает он, а не
    // регистрация.
    const poolInviteToken = readStoredInvite()
    if (poolInviteToken) {
      payload.pool_invite_token = poolInviteToken
    }

    // HK-IGRA.129: /profile/signup сам отправляет код и возвращает OTP-сессию
    // (ScopeAccount::$signup_send_login_code). Второго запроса на отправку кода
    // больше нет — именно он приходил с уже потраченным одноразовым
    // captcha-session и падал с «Сессия проверки истекла».
    //
    // IGRA-20: это одна форма и для новых, и для вернувшихся — человек не видит
    // разницы между «регистрацией» и «входом». Занятый email для бэкенда тоже
    // не ошибка: он молча шлёт код входа под существующий аккаунт.
    let result: Awaited<ReturnType<typeof profileStore.register>>
    try {
      result = await profileStore.register(payload, isCurrent)
    } catch (error: unknown) {
      if (version !== requestVersion) return
      // И истёкшая сессия капчи, и отказ невидимой Turnstile (HK-IGRA.940)
      // лечатся одинаково: окно видимой проверки и повтор с её токеном.
      if (!isCaptchaSessionExpiredError(error)) throw error
      const freshToken = await requestCaptchaToken()
      if (version !== requestVersion) return
      delete payload['captcha-session']
      delete payload[getCaptchaFieldName(token)]
      payload[getCaptchaFieldName(freshToken)] = freshToken
      result = await profileStore.register(payload, isCurrent)
    }
    if (version !== requestVersion) return

    // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать.
    // Поймать надо здесь: ниже начинается экран ввода кода, а кода нет.
    if (result?.choose) {
      resetOtpStatus()
      otpDigits.fill('')
      choiceChannels.value = result.choose.channels
      lastChoiceChannels.value = result.choose.channels
      choiceCaptchaSession.value = result.choose.captcha_session ?? null

      return
    }

    // Clear stored referral/circle-invite flags after successful registration
    localStorage.removeItem('partner_referral_code')
    localStorage.removeItem('circle_invite')

    // Сессия уже была (живой ключ в браузере): бэкенд кода не шлёт, а отвечает
    // `{redirect: '/', success: 1}` и возвращает тот же токен в x-token —
    // стор его уже сохранил и профиль подтянул (AccountAjaxController::actionSignup).
    // Ждать письмо здесь нечего: ведём туда же, куда и после верного кода.
    if (result?.redirect && result.otp_required !== 1) {
      if (await signInDone(router, null, isCurrent)) close()
      return
    }

    choiceChannels.value = []
    codeReceiver.value = result?.reciever === 'telegram' ? 'telegram' : 'email'
    receiverAddress.value = result?.reciever_address ?? ''
    step.value = 'otp'
    codeBurned.value = false
    otpDigits.fill('')
    startOtpStatus(form.email)
    nextTick(() => otpInputs.value[0]?.focus())
  } catch (error: unknown) {
    if (version !== requestVersion) return
    if (codeLimitOf(error) === 'throttled') {
      pauseResend()
      generalError.value = t('auth.codeLimit.tooManyRequests')
      return
    }
    const parsed = parseApiError(error)
    if (Object.keys(parsed.fieldErrors).length > 0) {
      fieldErrors.value = parsed.fieldErrors
    }
    generalError.value = parsed.message
  } finally {
    if (version === requestVersion) isLoading.value = false
  }
}

// Step 2: OTP — mandatory email confirmation (IGRA-20)
const otpDigits = reactive<string[]>(new Array(6).fill(''))
const otpInputs = ref<HTMLInputElement[]>([])
const isOtpComplete = computed(() => otpDigits.every((d) => d.length === 1))
const getOtpValue = () => otpDigits.join('')

// Повторная отправка — это тот же самый submit, что и на шаге email: капча,
// затем /profile/signup, который под уже существующий аккаунт просто шлёт новый
// код. Отдельного запроса на переотправку нет, а значит нет и второго
// предъявления одноразового captcha-session.
const handleResendOtp = () => {
  if (isLoading.value || isResendPaused.value) return
  generalError.value = null
  captchaWidgetRef.value?.showCaptcha?.()
}

/**
 * Человек выбрал канал. Повторяем регистрацию с этим каналом, но капчу
 * второй раз НЕ показываем: фаза выбора вернула свежую одноразовую сессию
 * капчи, её и отправляем. Вторая капча за один вход — трение ровно там,
 * где спринт его снимает.
 */
const handleChoicePick = (channel: 'telegram' | 'email') => {
  if (isLoading.value || !choiceChannels.value.includes(channel)) return
  chosenChannel.value = channel

  // Сессии нет — значит бэкенд её не выдал (перекос выката: новый фронт,
  // старый бэк). Тогда честнее показать капчу заново, чем отправить запрос
  // с пустым токеном и получить отказ.
  if (choiceCaptchaSession.value) {
    void onCaptchaVerified('')
  } else {
    captchaWidgetRef.value?.showCaptcha?.()
  }
}

/** Вернуться к выбору с экрана ввода кода. */
const switchChannel = () => {
  if (isLoading.value) return
  resetOtpStatus()
  otpDigits.fill('')
  clearErrors()
  choiceChannels.value = lastChoiceChannels.value
  chosenChannel.value = null
}

const handleOtpInput = (event: Event, index: number) => {
  const input = event.target as HTMLInputElement
  const val = input.value

  if (!/^\d*$/.test(val)) {
    otpDigits[index] = ''
    return
  }

  if (val.length === 1 && index < otpInputs.value.length - 1) {
    otpInputs.value[index + 1]?.focus()
  } else if (val.length > 1) {
    otpDigits[index] = val.slice(-1)
  }
}

const handleOtpDelete = (index: number) => {
  if (otpDigits[index]) {
    otpDigits[index] = ''
    return
  }
  if (index > 0) {
    otpDigits[index - 1] = ''
    otpInputs.value[index - 1]?.focus()
  }
}

const handleOtpPaste = (event: ClipboardEvent) => {
  const paste = event.clipboardData?.getData('text')
  if (!paste) return

  const numbers = paste.match(/\d/g)
  if (numbers) {
    const fieldsCount = otpInputs.value.length
    numbers.slice(0, fieldsCount).forEach((num, i) => {
      otpDigits[i] = num
    })
    otpInputs.value[Math.min(numbers.length, fieldsCount - 1)]?.focus()
    if (numbers.length >= fieldsCount) {
      nextTick(() => handleOtpSubmit())
    }
  }
}

/**
 * IGRA-388/389 — первый вход подтвердили другим каналом (бот, звонок).
 * Сессия садится тем же путём, что и по верному коду.
 */
watch(otpStatus, async (value) => {
  if (value !== 'confirmed' || !otpLoginToken.value || choiceChannels.value.length) return

  // OTP-R3: смена email, закрытие и unmount отменяют этот вход.
  const version = requestVersion
  const isCurrent = () => version === requestVersion
  isLoading.value = true
  try {
    const grant = await ProfileApi.loginByMagic(form.email, otpLoginToken.value, '', isCurrent)
    if (!isCurrent()) return
    if (grant.requires_approval) {
      enterTotpStep(grant)
      return
    }
    // FLOW-V5 Б1 — та же дорога «вход состоялся», что у верного кода.
    if (await signInDone(router, grant.token, isCurrent)) close()
  } catch (error: any) {
    if (!isCurrent()) return
    generalError.value = parseApiError(error).message
  } finally {
    if (isCurrent()) isLoading.value = false
  }
})

const handleOtpSubmit = async () => {
  if (!isOtpComplete.value || isLoading.value || codeBurned.value || choiceChannels.value.length || step.value !== 'otp') return

  // Код введён — судьба письма человека больше не занимает.
  stopOtpStatus()
  // OTP-R3: смена email, закрытие и unmount отменяют эту проверку кода.
  const version = requestVersion
  const isCurrent = () => version === requestVersion
  isLoading.value = true
  generalError.value = null

  try {
    const result = await ProfileApi.verifyLoginCode(form.email, getOtpValue(), isCurrent)
    if (!isCurrent()) return
    if (result.requires_approval) {
      enterTotpStep(result)
      return
    }

    // Это одна форма и для новых, и для вернувшихся (IGRA-20), поэтому решение
    // «кабинет или витрина» тоже одно — см. resolvePostAuthRoute.
    // FLOW-V5 Б1 — на том же адресе (страница переноса) не переходим, а
    // перечитываем профиль: иначе страница не узнала бы о входе.
    if (await signInDone(router, result.token, isCurrent)) close()
  } catch (error: any) {
    if (!isCurrent()) return
    const limit = codeLimitOf(error)
    if (limit === 'burned') codeBurned.value = true
    if (limit === 'throttled') pauseResend()
    generalError.value = limit === 'burned'
      ? t('auth.codeLimit.burned')
      : limit === 'throttled'
        ? t('auth.codeLimit.tooManyRequests')
        : parseApiError(error).message
    otpDigits.fill('')
    if (!codeBurned.value) nextTick(() => otpInputs.value[0]?.focus())
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

/**
 * OTP-R6c — первый фактор принят, но вход ещё не выдан. Для TOTP открываем шаг
 * кода из приложения; другой тип подтверждения этим ручкам не положен —
 * входа нет, человек видит ошибку и может начать заново.
 */
const enterTotpStep = (grant: { token: string; approval_type?: string }) => {
  stopOtpStatus()
  otpDigits.fill('')
  if (grant.approval_type !== 'totp') {
    generalError.value = t('auth.totpLogin.failed')
    return
  }
  pendingToken.value = grant.token
  step.value = 'totp'
}

const handleTotpApproved = async () => {
  const version = requestVersion
  const isCurrent = () => version === requestVersion
  // FLOW-V5 Б1 — после кода из приложения вход идёт той же дорогой.
  if (await signInDone(router, pendingToken.value, isCurrent)) close()
}

const handleTotpExpired = () => {
  goBackToEmail()
  generalError.value = t('auth.totpLogin.expired')
}
</script>

<style scoped lang="scss">
.modal-title {
  font-family: var(--ds-font-display);
  font-size: var(--p-text-28);
  font-weight: var(--ds-weight-bold);
  letter-spacing: -0.02em;
  text-align: center;
  margin-bottom: 12px;
  color: var(--ds-text);
}

.modal-subtitle {
  text-align: center;
  color: var(--ds-text-secondary);
  margin-bottom: 32px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.email-input-container {
  position: relative;
}

.form-label {
  display: block;
  font-weight: var(--ds-weight-semibold);
  margin-bottom: 8px;
  color: var(--ds-text);
}

.form-input {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-input-radius);
  background: var(--ds-overlay-04);
  color: var(--ds-text);
  font-size: var(--ds-text-size-body-lg);
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: var(--ds-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    box-shadow: var(--ds-focus-ring-shadow);
  }
}

.btn {
  padding: 14px;
  border-radius: var(--ds-radius-control);
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body-lg);
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block {
  width: 100%;
  display: block;
}

.btn-primary {
  background: linear-gradient(180deg, var(--ds-accent-light), var(--ds-accent));
  color: var(--ds-text-on-accent);
  box-shadow: var(--ds-glow-cta);

  &:hover {
    transform: translateY(-2px);
  }
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--ds-border);
  color: var(--ds-text);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;

  &:hover {
    background: var(--ds-surface-raised);
  }
}

.social-buttons {
  display: flex;
  gap: 16px;
  margin-top: 24px;
  justify-content: center;
}

.captcha-fallback {
  margin: 20px 0;
  padding: 16px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  background: var(--ds-surface-raised);
  text-align: center;

  &__title {
    font-weight: var(--ds-weight-semibold);
    margin-bottom: 12px;
    color: var(--ds-text);
  }

  &__widget {
    display: inline-block;
  }

  &__hint {
    margin-top: 12px;
    font-size: var(--p-text-13);
    color: var(--ds-text-muted);
  }
}

.social-btn {
  width: 60px;
  height: 48px;
  border-radius: var(--ds-radius-control);
  border: 1px solid var(--ds-border);
  background: var(--ds-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-size-h-md);
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--ds-surface-raised);
  }
}

.social-btn--wide {
  width: 100%;
  gap: 10px;
  padding: 0 16px;
}

.social-btn--wide span {
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
}

.social-btn.telegram {
  color: var(--ds-text-secondary);

  .telegram-icon {
    width: 18px;
    height: 18px;
    color: #0088cc;
  }
}

.divider {
  margin: 24px 0;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    background: var(--ds-border);
    z-index: 0;
  }

  span {
    background: var(--ds-surface);
    padding: 0 12px;
    color: var(--ds-text-muted);
    position: relative;
    z-index: 1;
  }
}


.auth-footer {
  text-align: center;
  margin-top: 24px;
  color: var(--ds-text-secondary);
  
  a {
    color: var(--ds-accent);
    text-decoration: none;
    font-weight: var(--ds-weight-semibold);
  }
}

.legal-text {
  margin-top: 32px;
  font-size: var(--ds-text-size-caption);
  color: var(--ds-text-muted);
  text-align: center;
  line-height: 1.4;

  a {
    color: var(--ds-accent);
    text-decoration: none;
  }
}

.alert {
  padding: 12px 16px;
  border-radius: var(--ds-radius-card);
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--ds-text-size-body);
  line-height: 1.4;
  
  &.alert-warning {
    background: var(--ds-warning-soft);
    color: var(--ds-warning);
    border: 1px solid var(--ds-warning-soft);
  }
  
  &.alert-error {
    background: var(--ds-danger-soft);
    color: var(--ds-danger);
    border: 1px solid var(--ds-danger-soft);
  }
  
  i {
    font-size: var(--ds-text-size-h-sm);
  }
}

.form-error {
  display: block;
  color: var(--ds-danger);
  font-size: var(--ds-text-size-caption);
  margin-top: 4px;
}

.form-input--error {
  border-color: var(--ds-danger) !important;

  &:focus {
    border-color: var(--ds-danger) !important;
  }
}

.form-input--highlight {
  border-color: var(--ds-accent) !important;
  box-shadow: 0 0 0 3px var(--ds-accent-soft-15);
}

.form-hint {
  display: block;
  color: var(--ds-accent);
  font-size: var(--ds-text-size-caption);
  margin-top: 6px;
}

.inviter-question {
  position: relative;
  z-index: 2;
  margin-bottom: 20px;
  padding: 20px;
  background: var(--ds-surface);
  border: 1px solid var(--ds-border);
  border-radius: var(--p-radius-14);
  text-align: center;
  box-shadow: 0 12px 30px var(--ds-accent-soft-12);

  &__text {
    margin: 0 0 16px;
    font-size: var(--p-text-15);
    font-weight: var(--ds-weight-semibold);
    color: var(--ds-text);
  }

  &__buttons {
    display: flex;
    gap: 12px;
  }

  &__btn {
    flex: 1;
  }
}

// Focus mode: when asking the inviter question, blur/dim every other element
// in the modal so the user's attention is locked on the Yes/No prompt. The
// close button stays fully visible so the user can still dismiss the modal.
.modal-content--asking {
  .modal-title,
  .modal-subtitle,
  .auth-form > *:not(.inviter-question),
  :deep(.social-buttons),
  .auth-footer,
  .legal-text {
    opacity: 0.35;
    filter: blur(2px);
    pointer-events: none;
    user-select: none;
    transition: opacity 0.2s ease, filter 0.2s ease;
  }
}

// Password step styles
.email-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--ds-surface-raised);
  border-radius: var(--ds-radius-card);
  
  span {
    color: var(--ds-text);
    font-weight: var(--ds-weight-medium);
  }
  
  a {
    color: var(--ds-accent);
    font-size: var(--ds-text-size-body);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.password-input-wrapper {
  position: relative;
  
  .form-input {
    padding-right: 48px;
  }
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--ds-text-muted);
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: var(--ds-text-secondary);
  }
  
  i {
    font-size: var(--ds-text-size-h-sm);
  }
}

.password-requirements {
  margin-top: 12px;
  padding: 12px;
  background: var(--ds-surface-raised);
  border-radius: var(--ds-radius-control);
  
  .requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--p-text-13);
    color: var(--ds-text-muted);
    padding: 4px 0;
    
    i {
      font-size: var(--ds-text-size-body-lg);
    }
    
    &.valid {
      color: var(--ds-success);
    }
  }
}

.auth-footer--links {
  display: flex;
  justify-content: center;
  gap: 16px;
  
  .separator {
    color: #ccc;
  }
  
  a {
    color: var(--ds-accent);
    font-size: var(--ds-text-size-body);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

/* Hide captcha widget - bind mode shows popup on showCaptcha() call */
.captcha-hidden-container {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

// OTP step (IGRA-20)
.code-digits {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.code-digit {
  width: 48px;
  height: 56px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  text-align: center;
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: var(--ds-text);
  background: var(--ds-surface-canvas);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    background: var(--ds-surface);
    box-shadow: 0 0 0 4px var(--ds-accent-soft-15);
  }
}

.resend-link {
  text-align: center;
  margin-top: 16px;

  a {
    color: var(--ds-accent);
    text-decoration: none;
    font-size: var(--ds-text-size-body);
    font-weight: var(--ds-weight-semibold);

    &:hover:not(.disabled) {
      text-decoration: underline;
    }

    &.disabled {
      color: var(--ds-text-muted);
      cursor: not-allowed;
    }
  }
}
</style>
